import { memo } from 'react';
import { css } from '@emotion/react';
import { CallEvent } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-path/v1/api.pb';
import dayjs from 'dayjs';
import { AiEventIcon } from '@frontend/phone-ui';
import { theme } from '@frontend/theme';
import { Text, Truncated } from '@frontend/design-system';
import { AIReceptionistEventComponent } from '../components/ai/ai-receptionist-accordion-event';
import { Dot } from '../components/dot.component';
import { IVREvent } from '../components/ivr/ivr-accordion-event';
import { VoicemailEvent } from '../components/voicemail/voicemail-accordion-event';

export const formatTime = (date?: string): string => {
  return date ? dayjs(date).format('hh:mm:ss A') : '';
};

interface DotRowProps {
  message: React.ReactNode;
  time: string;
  hideDot?: boolean;
}

interface EventRowProps {
  message: React.ReactNode;
  time: string;
  leading: 'dot' | 'icon' | 'none';
  iconEvent?: React.ComponentProps<typeof AiEventIcon>['event'];
  showConnector?: boolean;
}

interface EventRowListItem {
  message: React.ReactNode;
  time: string;
  iconEvent?: React.ComponentProps<typeof AiEventIcon>['event'];
}

interface EventRowListProps {
  events: EventRowListItem[];
}
// Remove this once we don't have to account for the air-event-ui wetag being disabled
export const DotRow = memo(({ message, time, hideDot }: DotRowProps) => (
  <div css={[styles, !hideDot && trailingDotStyles]}>
    <Dot className='dot' size='large' css={{ backgroundColor: theme.colors.neutral50 }} />
    <Truncated textAlign='left' as={Text} size='medium'>
      {message}
    </Truncated>
    <Text as='div' size='medium'>
      {time}
    </Text>
  </div>
));

const EventRow = memo(function EventRow({ message, time, leading, iconEvent, showConnector }: EventRowProps) {
  return (
    <div css={[styles, showConnector && trailingDotStyles]}>
      <span className='dot' css={markerStyles} aria-hidden={leading === 'none'}>
        {iconEvent ? (
          <span css={iconStyles}>
            <AiEventIcon event={iconEvent} />
          </span>
        ) : leading === 'dot' ? (
          <Dot size='large' css={{ backgroundColor: theme.colors.neutral50 }} />
        ) : leading === 'icon' ? (
          <span css={iconStyles}>
            <AiEventIcon event='added-to-schedule' />
          </span>
        ) : (
          <span css={leadingSpacerStyles} />
        )}
      </span>
      <Truncated textAlign='left' as={Text} size='medium'>
        {message}
      </Truncated>
      <Text as='div' size='medium'>
        {time}
      </Text>
    </div>
  );
});

export const EventRowList = memo(({ events }: EventRowListProps) => {
  if (events.length === 0) {
    return null;
  }

  const isSingleEvent = events.length === 1;

  return (
    <div>
      {events.map(({ message, time, iconEvent }, index) => {
        const isFirst = index === 0;
        const isLast = index === events.length - 1;
        const leading = iconEvent ? 'icon' : isSingleEvent ? 'none' : isFirst || isLast ? 'dot' : 'icon';

        return (
          <EventRow
            key={`${time}-${index}`}
            message={message}
            time={time}
            leading={leading}
            iconEvent={iconEvent}
            showConnector={!isLast && !isSingleEvent}
          />
        );
      })}
    </div>
  );
});

EventRowList.displayName = 'EventRowList';

export const getEventBody = (event: CallEvent, callerName?: string) => {
  if ('ivrEvent' in event) {
    return <IVREvent dtmfEvents={event.ivrEvent.dtmfEvents} callerName={callerName} />;
  }

  if ('voicemailPromptEvent' in event) {
    return (
      <VoicemailEvent
        transcription={event.voicemailPromptEvent.transcription}
        createdAt={event.createdAt}
        callerName={callerName}
      />
    );
  }

  if ('aiReceptionistEvent' in event) {
    return <AIReceptionistEventComponent {...event.aiReceptionistEvent} />;
  }

  return null;
};

EventRow.displayName = 'EventRow';
DotRow.displayName = 'DotRow';

const styles = css`
  grid-template-columns: max-content 1fr max-content;
  display: grid;
  gap: ${theme.spacing(1)};
  align-items: center;
`;

const markerStyles = css`
  position: relative;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
`;

const leadingSpacerStyles = css`
  width: 12px;
  height: 12px;
`;

const iconStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.neutral5};
  border-radius: ${theme.borderRadius.small};
`;

const trailingDotStyles = css`
  margin-bottom: ${theme.spacing(2)};

  > .dot::after {
    content: '';
    position: absolute;
    top: 115%;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 22px;
    background-image: repeating-linear-gradient(
      to bottom,
      ${theme.colors.neutral50},
      ${theme.colors.neutral50} 5px,
      transparent 5px,
      transparent 8px
    );
  }
`;
